import React from 'react';
import { Service } from '../../types';
import { Info } from 'lucide-react';

export function getQuoteRequestInstructionsText(service: Service | Record<string, unknown>): string {
  const s = service as Record<string, unknown>;
  const raw = s.quoteRequestInstructions ?? s.quote_request_instructions ?? '';
  return String(raw ?? '').trim();
}

interface QuoteRequestInstructionsPanelProps {
  services: Service[];
  brandColor: string;
  mutedColor?: string;
  borderColor?: string;
  surfaceColor?: string;
}

/**
 * One card per quotable service with non-empty instructions.
 * Used on the quote request step, stacked above the Details card (full width).
 */
const QuoteRequestInstructionsPanel: React.FC<QuoteRequestInstructionsPanelProps> = ({
  services,
  brandColor,
  mutedColor = '#6b7280',
  borderColor = '#e5e7eb',
  surfaceColor = '#f9fafb'
}) => {
  const items = services
    .map((service) => ({
      service,
      instructions: getQuoteRequestInstructionsText(service)
    }))
    .filter(({ service, instructions }) => (service as any)?.quotable === true && instructions.length > 0);

  if (items.length === 0) return null;

  return (
    <section className="w-full space-y-3" aria-label="Quote request instructions">
      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
        Quote request instructions
      </p>
      <div className="space-y-3">
        {items.map(({ service, instructions }) => (
          <article
            key={String(service.id)}
            className="rounded-xl border bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md"
            style={{ borderColor }}
          >
            <div
              className="px-4 py-3 flex items-center gap-3 border-b"
              style={{
                borderColor: `${brandColor}22`,
                background: `linear-gradient(135deg, ${brandColor}0f 0%, ${surfaceColor} 100%)`
              }}
            >
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: `${brandColor}18`, color: brandColor }}
                aria-hidden
              >
                <Info className="h-5 w-5" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1 flex items-center">
                <h3 className="text-sm font-semibold text-gray-900 leading-snug break-words">
                  {service.name}
                </h3>
              </div>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                {instructions}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default QuoteRequestInstructionsPanel;
